import { type Breakpoint } from '@divi/types';

import { type MultiViewDataBreakpointState, type MultiViewDataParsed } from '../../types';

/**
 * This function parses data based on a given breakpoint and returns the default and pseudo-state
 * data for that breakpoint.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to parse the data.
 * @param {MultiViewDataBreakpointState} params.data The data parameter is an object
 * containing the data for different breakpoints (phone, tablet, desktop) and their corresponding
 * states (default, hover, sticky).
 * @param {Breakpoint.Name} params.breakpoint The breakpoint parameter is a string that represents the current
 * device breakpoint, such as 'phone', 'tablet', or 'desktop'. It is used to determine which data to
 * extract from the MultiViewDataBreakpointState object.
 * @param {Breakpoint.Name} params.baseBreakpoint The base breakpoint.
 * @param {Breakpoint.Names} params.breakpointNames The names of the breakpoints.
 *
 * @returns {MultiViewDataParsed} The function `parseData` returns an object with state-specific
 * properties such as `defaultStateData`, `hoverStateData`, `focusStateData`, `checkedStateData`,
 * `activeStateData`, and `stickyStateData`. These properties contain the parsed data for the specified
 * breakpoint.
 */
export const parseData = <TItem>({
  data,
  breakpoint,
  baseBreakpoint = 'desktop',
  breakpointNames = ['desktop', 'tablet', 'phone'],
}: {
  data: MultiViewDataBreakpointState<TItem>;
  breakpoint: Breakpoint.Name;
  baseBreakpoint: Breakpoint.Name;
  breakpointNames: Breakpoint.Names;
}): MultiViewDataParsed<TItem> => {
  let defaultStateData: TItem = null;
  let hoverStateData: TItem = null;
  let focusStateData: TItem = null;
  let checkedStateData: TItem = null;
  let activeStateData: TItem = null;
  let stickyStateData: TItem = null;

  const breakpointIndex = breakpointNames.indexOf(breakpoint);
  const baseBreakpointIndex = breakpointNames.indexOf(baseBreakpoint);

  const isBreakpointLargerThanBase = breakpointIndex < baseBreakpointIndex;

  const filteredBreakpointNames = isBreakpointLargerThanBase
    ? // Add +1 on `end` param so the base breakpoint (`desktop`) is included on array of the filtered breakpoint names.
      breakpointNames.slice(breakpointIndex, baseBreakpointIndex + 1)
    : // Add +1 on `end` param so the last breakpoint is included on array of the filtered breakpoint names.
      breakpointNames.slice(baseBreakpointIndex, breakpointIndex + 1);

  if (!isBreakpointLargerThanBase) {
    filteredBreakpointNames.reverse();
  }

  filteredBreakpointNames.forEach(breakpointName => {
    const currentDefaultStateData = data?.[breakpointName as keyof MultiViewDataBreakpointState<TItem>];

    if ('undefined' !== typeof currentDefaultStateData && null === defaultStateData) {
      defaultStateData = currentDefaultStateData;
    }

    const currentHoverStateData = data?.[`${breakpointName}--hover` as keyof MultiViewDataBreakpointState<TItem>];

    if ('undefined' !== typeof currentHoverStateData && null === hoverStateData) {
      hoverStateData = currentHoverStateData;
    }

    const currentFocusStateData = data?.[`${breakpointName}--focus` as keyof MultiViewDataBreakpointState<TItem>];

    if ('undefined' !== typeof currentFocusStateData && null === focusStateData) {
      focusStateData = currentFocusStateData;
    }

    const currentCheckedStateData = data?.[`${breakpointName}--checked` as keyof MultiViewDataBreakpointState<TItem>];

    if ('undefined' !== typeof currentCheckedStateData && null === checkedStateData) {
      checkedStateData = currentCheckedStateData;
    }

    const currentActiveStateData = data?.[`${breakpointName}--active` as keyof MultiViewDataBreakpointState<TItem>];

    if ('undefined' !== typeof currentActiveStateData && null === activeStateData) {
      activeStateData = currentActiveStateData;
    }

    const currentStickyStateData = data?.[`${breakpointName}--sticky` as keyof MultiViewDataBreakpointState<TItem>];

    if ('undefined' !== typeof currentStickyStateData && null === stickyStateData) {
      stickyStateData = currentStickyStateData;
    }
  });

  const parsedData: MultiViewDataParsed<TItem> = {
    defaultStateData,
    hoverStateData,
    stickyStateData,
  };

  if (null !== focusStateData) {
    parsedData.focusStateData = focusStateData;
  }

  if (null !== checkedStateData) {
    parsedData.checkedStateData = checkedStateData;
  }

  if (null !== activeStateData) {
    parsedData.activeStateData = activeStateData;
  }

  return parsedData;
};
